import React, { useMemo, useState } from "react";
import {
  Modal,
  Button,
  Row,
  Col,
  Select,
  Tag,
  Space,
  Typography,
  Divider,
  Empty,
} from "antd";
import {
  ShoppingOutlined,
  FilterOutlined,
  CloseCircleOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import "./BusinessExpensesModal.scss";

const { Option } = Select;
const { Text, Title } = Typography;

interface DefaultValue {
  _id: string;
  clientId: string;
  supplierId: string;
  supplierName: string;
  locationId: string;
  locationName?: string;
  categoryName: string;
  defaultValue: number;
  startsAt?: string;
}

interface CustomValue {
  _id: string;
  clientId: string;
  supplierId: string;
  supplierName: string;
  locationId: string;
  locationName?: string;
  categoryName: string;
  amount: number;
  currency: string;
  description?: string;
  month: number; // Month number (1-12)
  year: number; // Year number (e.g., 2026)
}

interface DailyExpense {
  _id: string;
  date: string;
  description: string;
  amount: number;
  locationId: string;
  locationName?: string;
  supplierId?: string;
  supplierName?: string;
  categoryName?: string;
}

interface ExpenseItem {
  id: string;
  category: string;
  supplier: string;
  location: string;
  description: string;
  amount: number;
  currency: string;
  source: "default" | "custom" | "daily";
  month?: string;
  date?: string;
}

interface Props {
  visible: boolean;
  onClose: () => void;
  defaultValues: DefaultValue[];
  customValues: CustomValue[];
  dailyExpenses: DailyExpense[];
  locationFilters: string[];
  selectedMonths: string[];
  locations?: any[]; // For mapping locationId to locationName
  categories?: any[]; // For mapping supplierId to categoryName
  generationStatus?: Record<string, any>; // Keyed by "YYYY-MM"
}

export const BusinessExpensesModal: React.FC<Props> = ({
  visible,
  onClose,
  defaultValues,
  customValues,
  dailyExpenses,
  locationFilters,
  selectedMonths,
  locations = [],
  categories = [],
  generationStatus = {},
}) => {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<{
    category: string;
    supplier: string;
    source: string;
    location: string;
    currency: string;
  }>({
    category: "all",
    supplier: "all",
    source: "all",
    location: "all",
    currency: "all",
  });

  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  // Helper function to get location name by ID
  const getLocationName = (locationId: string | null | undefined): string => {
    if (!locationId) return t("Unknown Location");
    const location = locations.find((loc) => loc._id === locationId);
    return location?.name || t("Unknown Location");
  };

  // Helper function to get category name by supplier ID
  const getCategoryName = (supplierId: string | null | undefined): string => {
    if (!supplierId) return "Ostalo";
    for (const category of categories) {
      if (category.suppliers && Array.isArray(category.suppliers)) {
        const supplier = category.suppliers.find(
          (s: any) => s._id === supplierId,
        );
        if (supplier) return category.name;
      }
    }
    return "Ostalo";
  };

  // Build unified expense list
  const { expensesList, uniqueCategories, uniqueSuppliers, uniqueLocations } =
    useMemo(() => {
      const list: ExpenseItem[] = [];
      const categoriesSet = new Set<string>();
      const suppliersSet = new Set<string>();
      const locationsSet = new Set<string>();

      // Months that have already had fixed expenses generated
      const generatedMonths = new Set(Object.keys(generationStatus));

      // Filter by selected locations
      const filterByLocation = (locationId: string) => {
        // Match Finance.tsx logic: reject items without locationId
        if (!locationId) return false;
        if (locationFilters.length === 0) return true;
        return locationFilters.includes(locationId);
      };

      // 1) Default Values (fixed monthly costs)
      // Skip defaults for months that have been generated — those months already
      // have SupplierCustomValue records with type:"fixed" counted below.
      // This mirrors the Finance.tsx totalBusinessExpenses calculation to prevent
      // double-counting.
      const filteredDefaultValues = defaultValues.filter((dv) => {
        if (!filterByLocation(dv.locationId)) return false;
        const monthKey = (dv as any).month as string | undefined;
        if (monthKey && generatedMonths.has(monthKey)) return false;
        return true;
      });

      filteredDefaultValues.forEach((dv) => {
        const category = dv.categoryName || "Ostalo";
        const supplier = dv.supplierName || "Unknown";
        const location = dv.locationName || getLocationName(dv.locationId);

        categoriesSet.add(category);
        suppliersSet.add(supplier);
        locationsSet.add(location);

        list.push({
          id: dv._id,
          category,
          supplier,
          location,
          description: t("Fixed monthly cost"),
          amount: dv.defaultValue,
          currency: "RSD",
          source: "default",
        });
      });

      // 2) Custom Values (custom entries per month)
      const filteredCustomValues = customValues
        .filter((cv) => filterByLocation(cv.locationId))
        .filter((cv: any) => {
          // Filter by selected months if provided
          if (selectedMonths.length === 0) return true;
          // Custom values have month (number) and year (number), convert to "YYYY-MM" format
          const cvMonth = `${cv.year}-${String(cv.month).padStart(2, "0")}`;
          return selectedMonths.includes(cvMonth);
        });

      filteredCustomValues.forEach((cv: any) => {
        const category = cv.categoryName || getCategoryName(cv.supplierId);
        const supplier = cv.supplierName || "Unknown";
        const location = cv.locationName || getLocationName(cv.locationId);

        categoriesSet.add(category);
        suppliersSet.add(supplier);
        locationsSet.add(location);

        list.push({
          id: cv._id,
          category,
          supplier,
          location,
          description: cv.description || t("Custom entry"),
          amount: cv.amount,
          currency: cv.currency.toUpperCase(),
          source: "custom",
          month: `${cv.year}-${String(cv.month).padStart(2, "0")}`, // Format as YYYY-MM
        });
      });

      // 3) Daily Expenses (from daily revenue)
      dailyExpenses
        .filter((de) => filterByLocation(de.locationId))
        .forEach((de) => {
          const category = de.categoryName || getCategoryName(de.supplierId);
          const supplier = de.supplierName || t("Daily Revenue");
          const location = de.locationName || getLocationName(de.locationId);

          categoriesSet.add(category);
          suppliersSet.add(supplier);
          locationsSet.add(location);

          list.push({
            id: de._id,
            category,
            supplier,
            location,
            description: de.description || t("Daily expense"),
            amount: de.amount,
            currency: "RSD",
            source: "daily",
            date: de.date,
          });
        });

      return {
        expensesList: list,
        uniqueCategories: Array.from(categoriesSet).sort(),
        uniqueSuppliers: Array.from(suppliersSet).sort(),
        uniqueLocations: Array.from(locationsSet).sort(),
      };
    }, [
      defaultValues,
      customValues,
      dailyExpenses,
      locationFilters,
      selectedMonths,
      locations,
      categories,
      generationStatus,
      t,
    ]);

  // Apply filters
  const filteredList = useMemo(() => {
    return expensesList.filter((item) => {
      if (filters.category !== "all" && item.category !== filters.category)
        return false;
      if (filters.supplier !== "all" && item.supplier !== filters.supplier)
        return false;
      if (filters.source !== "all" && item.source !== filters.source)
        return false;
      if (filters.location !== "all" && item.location !== filters.location)
        return false;
      if (filters.currency !== "all" && item.currency !== filters.currency)
        return false;
      return true;
    });
  }, [expensesList, filters]);

  // Calculate totals by currency
  const totals = useMemo(() => {
    const result: Record<string, number> = {};
    filteredList.forEach((item) => {
      if (!result[item.currency]) {
        result[item.currency] = 0;
      }
      result[item.currency] += item.amount;
    });

    return result;
  }, [filteredList, expensesList, defaultValues, customValues, dailyExpenses]);

  // Group by category
  const groupedByCategory = useMemo(() => {
    const groups: Record<
      string,
      {
        items: ExpenseItem[];
        totalsByCurrency: Record<string, number>;
        id: string;
      }
    > = {};

    filteredList.forEach((item, idx) => {
      const key = item.category || `cat-${idx}`;
      if (!groups[key]) {
        groups[key] = { items: [], totalsByCurrency: {}, id: key };
      }
      groups[key].items.push(item);
      if (!groups[key].totalsByCurrency[item.currency]) {
        groups[key].totalsByCurrency[item.currency] = 0;
      }
      groups[key].totalsByCurrency[item.currency] += item.amount;
    });

    return Object.values(groups);
  }, [filteredList]);

  const formatCurrency = (amount: number, currency: string = "RSD") => {
    return `${new Intl.NumberFormat("sr-RS", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount)} ${currency}`;
  };

  const getSourceLabel = (source: string) => {
    switch (source) {
      case "default":
        return t("Fixed cost");
      case "custom":
        return t("Custom entry");
      case "daily":
        return t("Daily revenue");
      default:
        return source;
    }
  };

  const getSourceColor = (source: string) => {
    switch (source) {
      case "default":
        return "blue";
      case "custom":
        return "green";
      case "daily":
        return "orange";
      default:
        return "default";
    }
  };

  const handleResetFilters = () => {
    setFilters({
      category: "all",
      supplier: "all",
      source: "all",
      location: "all",
      currency: "all",
    });
  };

  const hasActiveFilters =
    filters.category !== "all" ||
    filters.supplier !== "all" ||
    filters.source !== "all" ||
    filters.location !== "all" ||
    filters.currency !== "all";

  const handleClose = () => {
    handleResetFilters();
    onClose();
  };

  if (!visible) return null;

  return (
    <Modal
      title={
        <div className="business-expenses-modal-header">
          <ShoppingOutlined className="header-icon" />
          <div className="header-text">
            <Title level={4} className="header-title">
              {t("Regular business expenses")}
            </Title>
            <Text type="secondary" className="header-subtitle">
              {t("Detailed breakdown by category and supplier")}
            </Text>
          </div>
        </div>
      }
      open={visible}
      onCancel={handleClose}
      width={1400}
      className="business-expenses-modal"
      footer={[
        <Button key="close" type="primary" onClick={handleClose}>
          {t("Close")}
        </Button>,
      ]}
    >
      {/* Summary Section */}
      <div className="modal-summary">
        <div className="summary-card">
          <Text type="secondary" className="summary-label">
            {t("Total Expenses")}
          </Text>
          <div className="summary-amounts">
            {Object.entries(totals).map(([currency, amount]) => (
              <div key={currency} className="summary-amount">
                <Text strong className="amount-value">
                  {formatCurrency(amount, currency)}
                </Text>
              </div>
            ))}
          </div>
        </div>
        <div className="summary-stats">
          <div className="stat-item">
            <Text type="secondary">{t("Categories")}</Text>
            <Text strong>{groupedByCategory.length}</Text>
          </div>
          <div className="stat-item">
            <Text type="secondary">{t("Items")}</Text>
            <Text strong>{filteredList.length}</Text>
          </div>
        </div>
      </div>

      <Divider className="section-divider" />

      {/* Filters Section */}
      <div className="filters-section">
        <div className="filters-header">
          <div className="filters-title">
            <FilterOutlined />
            <Text strong>{t("Filters")}</Text>
          </div>
          {hasActiveFilters && (
            <Button
              size="small"
              icon={<CloseCircleOutlined />}
              onClick={handleResetFilters}
              className="reset-button"
            >
              {t("Reset filters")}
            </Button>
          )}
        </div>

        <Row gutter={[12, 12]} className="filters-row">
          {/* Category Filter */}
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-item">
              <Text className="filter-label">{t("Category")}</Text>
              <Select
                showSearch
                value={filters.category}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, category: value }))
                }
                className="filter-select"
                placeholder={t("All categories")}
                filterOption={(input, option: any) => {
                  if (option?.value === "all") return true;
                  return (option?.value?.toLowerCase() ?? "").includes(
                    input.toLowerCase(),
                  );
                }}
              >
                <Option value="all">
                  <Text strong style={{ color: "#1890ff" }}>
                    {t("All categories")}
                  </Text>
                </Option>
                {uniqueCategories.map((cat) => (
                  <Option key={cat} value={cat}>
                    {cat}
                  </Option>
                ))}
              </Select>
            </div>
          </Col>

          {/* Supplier Filter */}
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-item">
              <Text className="filter-label">{t("Supplier")}</Text>
              <Select
                showSearch
                value={filters.supplier}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, supplier: value }))
                }
                className="filter-select"
                placeholder={t("All suppliers")}
                filterOption={(input, option: any) => {
                  if (option?.value === "all") return true;
                  return (option?.value?.toLowerCase() ?? "").includes(
                    input.toLowerCase(),
                  );
                }}
              >
                <Option value="all">
                  <Text strong style={{ color: "#1890ff" }}>
                    {t("All suppliers")}
                  </Text>
                </Option>
                {uniqueSuppliers.map((sup) => (
                  <Option key={sup} value={sup}>
                    {sup}
                  </Option>
                ))}
              </Select>
            </div>
          </Col>

          {/* Source Filter */}
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-item">
              <Text className="filter-label">{t("Source")}</Text>
              <Select
                value={filters.source}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, source: value }))
                }
                className="filter-select"
                placeholder={t("All sources")}
              >
                <Option value="all">
                  <Text strong style={{ color: "#1890ff" }}>
                    {t("All sources")}
                  </Text>
                </Option>
                <Option value="default">{t("Fixed cost")}</Option>
                <Option value="custom">{t("Custom entry")}</Option>
                <Option value="daily">{t("Daily revenue")}</Option>
              </Select>
            </div>
          </Col>

          {/* Location Filter */}
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-item">
              <Text className="filter-label">{t("Location")}</Text>
              <Select
                showSearch
                value={filters.location}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, location: value }))
                }
                className="filter-select"
                placeholder={t("All locations")}
                filterOption={(input, option: any) => {
                  if (option?.value === "all") return true;
                  return (option?.value?.toLowerCase() ?? "").includes(
                    input.toLowerCase(),
                  );
                }}
              >
                <Option value="all">
                  <Text strong style={{ color: "#1890ff" }}>
                    {t("All locations")}
                  </Text>
                </Option>
                {uniqueLocations.map((loc) => (
                  <Option key={loc} value={loc}>
                    {loc}
                  </Option>
                ))}
              </Select>
            </div>
          </Col>

          {/* Currency Filter */}
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-item">
              <Text className="filter-label">{t("Currency")}</Text>
              <Select
                value={filters.currency}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, currency: value }))
                }
                className="filter-select"
                placeholder={t("All currencies")}
              >
                <Option value="all">
                  <Text strong style={{ color: "#1890ff" }}>
                    {t("All currencies")}
                  </Text>
                </Option>
                <Option value="RSD">RSD</Option>
                <Option value="EUR">EUR</Option>
              </Select>
            </div>
          </Col>
        </Row>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="active-filters">
            <Space wrap size={[8, 8]}>
              {filters.category !== "all" && (
                <Tag
                  color="blue"
                  closable
                  onClose={() =>
                    setFilters((prev) => ({ ...prev, category: "all" }))
                  }
                  className="filter-tag"
                >
                  {t("Category")}: {filters.category}
                </Tag>
              )}
              {filters.supplier !== "all" && (
                <Tag
                  color="green"
                  closable
                  onClose={() =>
                    setFilters((prev) => ({ ...prev, supplier: "all" }))
                  }
                  className="filter-tag"
                >
                  {t("Supplier")}: {filters.supplier}
                </Tag>
              )}
              {filters.source !== "all" && (
                <Tag
                  color="orange"
                  closable
                  onClose={() =>
                    setFilters((prev) => ({ ...prev, source: "all" }))
                  }
                  className="filter-tag"
                >
                  {t("Source")}: {getSourceLabel(filters.source)}
                </Tag>
              )}
              {filters.location !== "all" && (
                <Tag
                  color="purple"
                  closable
                  onClose={() =>
                    setFilters((prev) => ({ ...prev, location: "all" }))
                  }
                  className="filter-tag"
                >
                  {t("Location")}: {filters.location}
                </Tag>
              )}
              {filters.currency !== "all" && (
                <Tag
                  color="cyan"
                  closable
                  onClose={() =>
                    setFilters((prev) => ({ ...prev, currency: "all" }))
                  }
                  className="filter-tag"
                >
                  {t("Currency")}: {filters.currency}
                </Tag>
              )}
            </Space>
          </div>
        )}
      </div>

      <Divider className="section-divider" />

      {/* Expenses List */}
      <div className="expenses-list">
        {filteredList.length === 0 ? (
          <Empty
            description={t("No expenses found with current filters")}
            className="empty-state"
          />
        ) : (
          groupedByCategory.map((group) => {
            const isOpen = expandedCategory === group.id;

            return (
              <div
                key={group.id}
                className={`category-group ${isOpen ? "expanded" : ""}`}
              >
                <div
                  className="category-header"
                  onClick={() =>
                    setExpandedCategory((prev) =>
                      prev === group.id ? null : group.id,
                    )
                  }
                >
                  <div className="category-info">
                    <span className="expand-icon">{isOpen ? "▼" : "▶"}</span>
                    <Text strong className="category-name">
                      {group.id}
                    </Text>
                    <Tag className="items-count">{group.items.length}</Tag>
                  </div>
                  <div className="category-totals">
                    {Object.entries(group.totalsByCurrency).map(
                      ([currency, amount]) => (
                        <Text key={currency} strong className="category-amount">
                          {formatCurrency(amount, currency)}
                        </Text>
                      ),
                    )}
                  </div>
                </div>

                {isOpen && (
                  <div className="category-content">
                    {group.items.map((item, idx) => (
                      <div key={`${item.id}-${idx}`} className="expense-item">
                        <div className="expense-main">
                          <div className="expense-info">
                            <Text strong className="expense-supplier">
                              {item.supplier}
                            </Text>
                            <Text className="expense-description">
                              {item.description}
                            </Text>
                          </div>
                          <div className="expense-meta">
                            <Tag
                              color={getSourceColor(item.source)}
                              className="source-tag"
                            >
                              {getSourceLabel(item.source)}
                            </Tag>
                            <Text strong className="expense-amount">
                              {formatCurrency(item.amount, item.currency)}
                            </Text>
                          </div>
                        </div>
                        <div className="expense-details">
                          <Space size={[16, 8]} wrap>
                            <div className="detail-item">
                              <EnvironmentOutlined className="detail-icon" />
                              <Text type="secondary" className="detail-text">
                                {item.location}
                              </Text>
                            </div>
                            {item.month && (
                              <div className="detail-item">
                                <CalendarOutlined className="detail-icon" />
                                <Text type="secondary" className="detail-text">
                                  {item.month}
                                </Text>
                              </div>
                            )}
                            {item.date && (
                              <div className="detail-item">
                                <CalendarOutlined className="detail-icon" />
                                <Text type="secondary" className="detail-text">
                                  {new Date(item.date).toLocaleDateString(
                                    "sr-RS",
                                  )}
                                </Text>
                              </div>
                            )}
                            <div className="detail-item">
                              <DollarOutlined className="detail-icon" />
                              <Text type="secondary" className="detail-text">
                                {item.currency}
                              </Text>
                            </div>
                          </Space>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </Modal>
  );
};

export default BusinessExpensesModal;
